/**
 * MuJoCo initialization module.
 * Handles loading and initialization of the MuJoCo WASM module and application.
 */

import { MujocoApp } from './MujocoApp';
import { loadMujoco } from './mujocoTypes';
import type { MainModule, EmscriptenFS } from './mujocoTypes';
import type { CameraMode } from '@/types/scene.types';
import { extractTarToFilesystem, discoverXmlFile } from './tarExtractor';
import { getWorkingDirForTar, ensureDirectory } from './filesystemUtils';

type MujocoState = 'uninitialized' | 'loading' | 'ready' | 'error';

/**
 * Safely converts an unknown error to a string for logging
 */
function errorToString(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

let mujoco: MainModule | null = null;
let mujocoApp: MujocoApp | null = null;
let containerElementId: string | null = null;
let currentState: MujocoState = 'uninitialized';

// Global robot state
let robotLoaded = false;
const ROBOT_WORKING_DIR = '/working/franka_droid';
const ROBOT_TAR_PATH = '/api/scenes/tar/franka_droid_small';

// Preload tracking with working directory info
const preloadPromises = new Map<
  string,
  Promise<{ xmlContent: string; xmlPath: string; workingDir: string }>
>();

// Maximum number of scenes to keep in preload cache (LRU eviction)
const MAX_PRELOAD_CACHE_SIZE = 5;

// Track preload order for LRU eviction
const preloadOrder: string[] = [];

interface TarModelSource {
  type: 'tar';
  tarPath: string;
}

type ModelSource = TarModelSource;

/**
 * Loads the global robot model into the filesystem.
 * This should be called once before loading any scenes.
 * The robot files are extracted to /working/franka_droid and can be
 * referenced by all scenes via relative paths.
 *
 * @param mujoco - The MuJoCo WASM module
 * @returns Promise resolving when robot is loaded
 */
async function loadGlobalRobot(mujoco: MainModule): Promise<void> {
  if (robotLoaded) {
    console.log('Robot already loaded, skipping...');
    return;
  }

  try {
    console.log('Loading global robot model...');

    // Ensure robot directory exists
    ensureDirectory(mujoco.FS, ROBOT_WORKING_DIR);

    // Extract robot TAR to dedicated directory
    await extractTarToFilesystem(mujoco.FS, ROBOT_TAR_PATH, ROBOT_WORKING_DIR);

    robotLoaded = true;
    console.log(`Robot loaded successfully to ${ROBOT_WORKING_DIR}`);
  } catch (error) {
    console.error('Failed to load global robot:', error);
    throw new Error(`Robot loading failed: ${errorToString(error)}`);
  }
}

/**
 * Internal: Extracts TAR and reads XML file from filesystem
 * Shared by both preload and load operations
 */
async function extractAndReadXml(
  mujoco: MainModule,
  tarPath: string,
  targetDir: string
): Promise<{ xmlContent: string; xmlPath: string }> {
  const typedFS = mujoco.FS as unknown as EmscriptenFS;

  // Extract TAR file to target directory
  console.log(`Extracting TAR file: ${tarPath} to ${targetDir}`);
  await extractTarToFilesystem(mujoco.FS, tarPath, targetDir);
  console.log('TAR extraction complete');

  // Auto-discover XML file (all scenes have robots now)
  console.log('Auto-discovering XML file...');
  const discoveredFile = discoverXmlFile(mujoco.FS, targetDir);
  if (!discoveredFile) {
    throw new Error('No XML file found in extracted TAR');
  }
  console.log(`Found XML file: ${discoveredFile}`);

  const xmlPath = `${targetDir}/${discoveredFile}`;
  let xmlContent: string;
  try {
    xmlContent = typedFS.readFile(xmlPath, { encoding: 'utf8' }) as string;
  } catch {
    throw new Error(`Failed to read XML file: ${xmlPath}`);
  }
  console.log(`Loaded XML from filesystem: ${xmlPath}`);

  return { xmlContent, xmlPath };
}

/**
 * Loads model XML from a TAR source, using preloaded data if available
 */
async function loadModelFromTarSource(
  mujoco: MainModule,
  tarSource: TarModelSource
): Promise<{ modelXml: string; xmlPath: string; workingDir: string }> {
  // Generate unique working directory for this TAR
  const workingDir = getWorkingDirForTar(tarSource.tarPath);

  // Check if preloaded
  const cacheKey = tarSource.tarPath;
  const preloadPromise = preloadPromises.get(cacheKey);

  if (preloadPromise) {
    // Check if preloaded data is still valid (files exist in filesystem)
    console.log('Checking preloaded TAR data...');
    try {
      const preloaded = await preloadPromise;

      // Verify that the XML file still exists in the filesystem
      const typedFS = mujoco.FS as unknown as EmscriptenFS;
      try {
        typedFS.stat(preloaded.xmlPath);
        console.log('Using preloaded TAR data (verified in filesystem)');

        return {
          modelXml: preloaded.xmlContent,
          xmlPath: preloaded.xmlPath,
          workingDir: preloaded.workingDir,
        };
      } catch {
        console.warn('Preloaded files no longer in filesystem, re-extracting...');
        // Files were cleared, need to re-extract
        // Fall through to extraction
      }
    } catch (error) {
      console.warn('Preload failed, extracting now:', error);
      // Fall through to extraction
    }
  }

  // Not preloaded or preload failed, extract now
  ensureDirectory(mujoco.FS, workingDir);
  const result = await extractAndReadXml(mujoco, tarSource.tarPath, workingDir);

  return {
    modelXml: result.xmlContent,
    xmlPath: result.xmlPath,
    workingDir,
  };
}

/**
 * Initializes the MuJoCo simulation with a given model.
 * Creates a singleton MujocoApp instance that can be reused across scene changes.
 *
 * @param containerElementId - The DOM element ID where the renderer will be attached
 * @param modelSource - The model source (TAR file with path and optional XML filename)
 * @param cameraMode - The initial camera mode
 * @returns Promise resolving to the MujocoApp instance
 */
export async function initializeMujoco(
  containerElementIdParam: string,
  modelSource: ModelSource,
  cameraMode: CameraMode = 'top-down'
): Promise<MujocoApp> {
  try {
    console.log('Initializing MuJoCo...');
    currentState = 'loading';

    // Load the MuJoCo WASM module if not already loaded
    if (!mujoco) {
      try {
        mujoco = await loadMujoco();
        console.log('MuJoCo WASM module loaded successfully');
      } catch (error) {
        currentState = 'error';
        throw new Error(`Failed to load MuJoCo WASM module: ${errorToString(error)}`);
      }
    }

    // Load global robot (once per session)
    try {
      await loadGlobalRobot(mujoco);
    } catch (error) {
      // Robot loading failure shouldn't prevent scene loading
      // Scenes without robots will still work
      console.warn('Robot loading failed, continuing without robot:', error);
    }

    // Create the MujocoApp instance only if it doesn't exist or container changed
    if (!mujocoApp || containerElementId !== containerElementIdParam) {
      // If container changed, we need to move the canvas
      if (mujocoApp && containerElementId !== containerElementIdParam) {
        console.log('Container element changed, moving canvas...');
        const oldElement = document.getElementById(containerElementId || '');
        const newElement = document.getElementById(containerElementIdParam);
        if (
          oldElement &&
          newElement &&
          mujocoApp.renderer.domElement.parentElement === oldElement
        ) {
          oldElement.removeChild(mujocoApp.renderer.domElement);
          newElement.appendChild(mujocoApp.renderer.domElement);
        }
        containerElementId = containerElementIdParam;
      } else {
        // Create new app instance
        try {
          mujocoApp = new MujocoApp(mujoco, containerElementIdParam, cameraMode);
          containerElementId = containerElementIdParam;
          mujocoApp.run();
        } catch (error) {
          currentState = 'error';
          mujocoApp = null;
          containerElementId = null;
          throw new Error(`Failed to create MujocoApp: ${errorToString(error)}`);
        }
      }
    } else {
      // App already exists, just update camera mode if needed
      mujocoApp.setCameraMode(cameraMode);
    }

    // Load the model from TAR source (model-specific errors shouldn't kill the app)
    let modelXml: string;
    let xmlPath: string;
    try {
      const result = await loadModelFromTarSource(mujoco, modelSource);
      modelXml = result.modelXml;
      xmlPath = result.xmlPath;
    } catch (error) {
      // Model load failed, but app is still alive for retry
      currentState = 'error';
      throw new Error(`Failed to load model: ${errorToString(error)}`);
    }

    // Load or swap the model into MuJoCo
    try {
      if (mujocoApp.mjModel) {
        console.log('Swapping model in existing MujocoApp...');
        mujocoApp.swapModel(modelXml, xmlPath);
        // Ensure camera mode is set after swap
        mujocoApp.setCameraMode(cameraMode);
      } else {
        console.log('Loading model into MuJoCo...');
        mujocoApp.loadModel(modelXml, xmlPath);
      }
      console.log('Model loaded successfully');
      currentState = 'ready';
    } catch (error) {
      // Model swap failed, but app is still alive for retry
      currentState = 'error';
      throw new Error(`Failed to load/swap model: ${errorToString(error)}`);
    }

    return mujocoApp;
  } catch (error) {
    console.error('Initialization error: ', error);
    // Only dispose if it's a critical WASM or app creation failure
    // For model load failures, keep app alive for retry
    if (currentState === 'error' && !mujocoApp) {
      // App was never created or WASM failed, clean up completely
      mujoco = null;
      containerElementId = null;
    }
    throw error;
  }
}

/**
 * Swaps the model in the existing MujocoApp instance without recreating it.
 * This is more efficient than calling initializeMujoco again.
 *
 * @param modelSource - The model source (TAR file with path and optional XML filename)
 * @returns Promise resolving to the MujocoApp instance
 */
export async function swapMujocoModel(
  modelSource: ModelSource,
  cameraMode?: CameraMode
): Promise<MujocoApp> {
  if (!mujocoApp) {
    throw new Error('MujocoApp not initialized. Call initializeMujoco first.');
  }

  if (!mujoco) {
    throw new Error('MuJoCo WASM module not loaded.');
  }

  currentState = 'loading';

  try {
    // Load the model from TAR source
    const { modelXml, xmlPath } = await loadModelFromTarSource(mujoco, modelSource);

    // Swap the model
    mujocoApp.swapModel(modelXml, xmlPath);

    // Ensure camera mode is set after swap (prevents XML defaults from taking over)
    if (cameraMode) {
      mujocoApp.setCameraMode(cameraMode);
    }

    currentState = 'ready';
    return mujocoApp;
  } catch (error) {
    currentState = 'error';
    console.error('Model swap error: ', error);
    // Don't dispose app on swap failure - it can be retried
    throw error;
  }
}

/**
 * Shows the MuJoCo canvas
 */
export function showMujocoCanvas(): void {
  if (mujocoApp) {
    mujocoApp.showCanvas();
  }
}

/**
 * Hides the MuJoCo canvas without disposing resources
 */
export function hideMujocoCanvas(): void {
  if (mujocoApp) {
    mujocoApp.hideCanvas();
  }
}

/**
 * Gets the current MujocoApp instance if it exists.
 */
export function getMujocoApp(): MujocoApp | null {
  return mujocoApp;
}

/**
 * Clears the oldest preloaded TAR from cache to manage memory.
 * Uses LRU (Least Recently Used) eviction.
 */
function evictOldestPreload(): void {
  if (preloadOrder.length === 0) return;

  const oldestKey = preloadOrder.shift(); // Remove first (oldest)
  if (oldestKey) {
    preloadPromises.delete(oldestKey);
    console.log(`Evicted preload from cache: ${oldestKey}`);
  }
}

/**
 * Clears the preload cache to free memory.
 * Call this when switching away from the app or to force a refresh.
 */
export function clearPreloadCache(): void {
  preloadPromises.clear();
  preloadOrder.length = 0;
  console.log('Preload cache cleared');
}

/**
 * Clears all caches: TAR file cache, preload cache, and optionally MujocoApp caches.
 * Useful for debugging or forcing a complete refresh.
 *
 * @param includeAppCache - Whether to also clear MujocoApp's geometry/texture caches
 * @returns Promise resolving when all caches are cleared
 */
export async function clearAllCaches(includeAppCache: boolean = false): Promise<void> {
  console.log('🧹 Clearing all MuJoCo caches...');

  // Import cache functions
  const { clearTarCache } = await import('./tarExtractor');

  // Clear browser TAR cache
  const tarCacheCleared = await clearTarCache();
  console.log(`  - TAR file cache: ${tarCacheCleared ? 'cleared' : 'not found'}`);

  // Clear preload cache
  clearPreloadCache();
  console.log(`  - Preload cache: cleared`);

  // Optionally clear MujocoApp caches
  if (includeAppCache && mujocoApp) {
    // Clear texture caches
    mujocoApp.textureCache.clear();
    mujocoApp.textureCacheByFilename.clear();
    mujocoApp.bufferGeometryCache.clear();
    console.log(`  - MujocoApp caches: cleared`);
  }

  console.log('✅ All caches cleared successfully');
}

/**
 * Preloads a TAR file by extracting it into the filesystem.
 * This can be called in the background before the user clicks to start the simulation.
 *
 * @param tarPath - Path to the TAR file
 * @returns Promise resolving to the extracted XML content and path
 */
export async function preloadTarModel(
  tarPath: string
): Promise<{ xmlContent: string; xmlPath: string; workingDir: string }> {
  // Check if already preloading or preloaded
  const cacheKey = tarPath;
  const existingPromise = preloadPromises.get(cacheKey);
  if (existingPromise) {
    console.log('TAR model already preloading, returning existing promise');
    // Move to end of LRU list (mark as recently used)
    const index = preloadOrder.indexOf(cacheKey);
    if (index > -1) {
      preloadOrder.splice(index, 1);
      preloadOrder.push(cacheKey);
    }
    return existingPromise;
  }

  // Evict oldest preload if cache is full
  if (preloadPromises.size >= MAX_PRELOAD_CACHE_SIZE) {
    evictOldestPreload();
  }

  const preloadPromise = (async () => {
    try {
      console.log(`Preloading TAR file: ${tarPath}`);

      // Load MuJoCo WASM module if not already loaded
      if (!mujoco) {
        mujoco = await loadMujoco();
      }

      // Generate unique working directory for this TAR
      const workingDir = getWorkingDirForTar(tarPath);
      ensureDirectory(mujoco.FS, workingDir);

      // Use shared extraction logic
      const result = await extractAndReadXml(mujoco, tarPath, workingDir);

      console.log(`Preload complete: ${tarPath}`);
      return {
        xmlContent: result.xmlContent,
        xmlPath: result.xmlPath,
        workingDir,
      };
    } catch (error) {
      // Remove from cache on error so it can be retried
      preloadPromises.delete(cacheKey);
      const index = preloadOrder.indexOf(cacheKey);
      if (index > -1) {
        preloadOrder.splice(index, 1);
      }
      throw error;
    }
  })();

  preloadPromises.set(cacheKey, preloadPromise);
  preloadOrder.push(cacheKey); // Add to end of LRU list
  return preloadPromise;
}

// Expose cache management functions globally for debugging
declare global {
  interface Window {
    clearAllCaches: (includeAppCache?: boolean) => Promise<void>;
    clearPreloadCache: () => void;
    getMujocoApp: () => MujocoApp | null;
  }
}

if (typeof window !== 'undefined') {
  // Expose cache management and app instance for debugging
  (window as any).clearAllCaches = clearAllCaches;
  (window as any).clearPreloadCache = clearPreloadCache;
  (window as any).getMujocoApp = getMujocoApp;
}
